<template>
    <main class="flex-1 relative overflow-y-auto py-6 focus:outline-none" tabindex="0">
        <div class="max-w-4xl mx-auto sm:px-6 lg:px-8 px-5 print:hidden">
            <div class="md:flex md:items-center md:justify-between">
                <div class="flex-1 min-w-0">
                    <h1 class="py-0.5 text-2xl font-semibold text-gray-900">{{ $t('IOM details') }}</h1>
                </div>
                <div class="mt-4 flex flex-wrap md:mt-0 md:ml-4 gap-3">
                    <button
                        class="btn btn-secondary shadow-sm rounded-md"
                        type="button"
                        @click="copyNumber"
                    >
                        {{ $t('Copy number') }}
                    </button>
                    <button
                        class="btn btn-secondary shadow-sm rounded-md"
                        type="button"
                        @click="printMemo"
                    >
                        {{ $t('Print') }}
                    </button>
                    <router-link
                        v-if="iom.can_edit"
                        class="btn btn-blue shadow-sm rounded-md"
                        :to="'/dashboard/iom/' + $route.params.uuid + '/edit'"
                    >
                        {{ $t('Edit IOM') }}
                    </router-link>
                    <button
                        v-if="iom.can_delete"
                        class="btn btn-red shadow-sm rounded-md"
                        type="button"
                        @click="deleteIom"
                    >
                        {{ $t('Delete IOM') }}
                    </button>
                </div>
            </div>
        </div>

        <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div class="mt-6 shadow sm:rounded-lg bg-white">
                <loading :status="loading"/>
                <div v-if="!loading && iom.uuid" id="iom-print-area" class="px-6 py-8 sm:px-10 sm:py-10">
                    <!-- Kop memo -->
                    <div class="border-b-2 border-gray-800 pb-4">
                        <div class="text-center">
                            <div class="text-lg font-bold uppercase tracking-wide text-gray-900">
                                {{ $t('Internal Office Memo') }}
                            </div>
                            <div class="mt-1 text-sm font-semibold text-gray-700">{{ iom.number }}</div>
                        </div>
                    </div>

                    <!-- Kepada / tanggal / perihal -->
                    <div class="mt-6 grid grid-cols-1 sm:grid-cols-4 gap-y-3 gap-x-4 text-sm">
                        <div class="sm:col-span-1 font-semibold text-gray-700">{{ $t('To') }}</div>
                        <div class="sm:col-span-3 text-gray-900">
                            <span v-if="iom.departments.length > 0">{{ departmentNames }}</span>
                            <span v-else class="text-gray-400">{{ $t('No divisions assigned') }}</span>
                        </div>

                        <div class="sm:col-span-1 font-semibold text-gray-700">{{ $t('Memo date') }}</div>
                        <div class="sm:col-span-3 text-gray-900">{{ iom.memo_date_formatted }}</div>

                        <div class="sm:col-span-1 font-semibold text-gray-700">{{ $t('Subject') }}</div>
                        <div class="sm:col-span-3 text-gray-900">{{ iom.subject }}</div>
                    </div>

                    <!-- Isi memo -->
                    <div class="mt-8 border-t border-gray-200 pt-6 text-sm leading-6 text-gray-800 iom-content" v-html="iom.content"></div>

                    <!-- Tanda tangan pembuat, mengikuti lazimnya memo internal -->
                    <div class="mt-12 flex justify-end">
                        <div class="text-sm text-gray-800 text-center">
                            <div>{{ iom.memo_date_formatted }}</div>
                            <div class="mt-16 font-semibold underline">{{ creatorName }}</div>
                        </div>
                    </div>

                    <div v-if="iom.created_at" class="mt-10 text-xs text-gray-400">
                        {{ $t('Created at') }}: {{ formatDateTime(iom.created_at) }}
                    </div>
                </div>
            </div>

            <!-- Penerima memo: siapa saja di divisi tujuan, beserta rolenya.
                 Sengaja di luar #iom-print-area karena ini info internal,
                 bukan bagian dari dokumen memo resmi. -->
            <div v-if="!loading && iom.addressees && iom.addressees.length > 0" class="mt-6 shadow sm:rounded-lg bg-white print:hidden">
                <div class="px-4 py-5 sm:p-6">
                    <h3 class="text-lg font-medium leading-6 text-gray-900">{{ $t('Memo recipients') }}</h3>
                    <p class="mt-1 text-sm leading-5 text-gray-500">
                        {{ $t('Users in the selected divisions who receive this memo') }}.
                    </p>
                    <div class="mt-4 overflow-x-auto">
                        <table class="min-w-full divide-y divide-gray-200">
                            <thead>
                                <tr>
                                    <th class="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">{{ $t('Name') }}</th>
                                    <th class="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">{{ $t('Role') }}</th>
                                    <th class="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">{{ $t('Email') }}</th>
                                </tr>
                            </thead>
                            <tbody class="divide-y divide-gray-100">
                                <tr v-for="addressee in iom.addressees" :key="'iom-addressee-' + addressee.id">
                                    <td class="px-3 py-2 text-sm text-gray-900">{{ addressee.name }}</td>
                                    <td class="px-3 py-2 text-sm text-gray-700">
                                        <span v-if="addressee.role_name" class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                            {{ addressee.role_name }}
                                        </span>
                                        <span v-else class="text-gray-400">—</span>
                                    </td>
                                    <td class="px-3 py-2 text-sm text-gray-500">{{ addressee.email }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    </main>
</template>

<script>
export default {
    name: "iom-manage",
    metaInfo() {
        return {
            title: this.$i18n.t('IOM details')
        }
    },
    data() {
        return {
            loading: true,
            iom: {
                uuid: null,
                number: null,
                subject: null,
                memo_date_formatted: null,
                content: '',
                departments: [],
                addressees: [],
                can_edit: false,
                can_delete: false,
                created_at: null,
            },
        }
    },
    computed: {
        departmentNames() {
            return this.iom.departments.map(function (department) {
                return department.name;
            }).join(', ');
        },
        creatorName() {
            return this.iom.user ? this.iom.user.name : '';
        },
    },
    mounted() {
        this.getIom();
    },
    methods: {
        getIom() {
            const self = this;
            self.loading = true;
            axios.get('api/dashboard/iom/' + self.$route.params.uuid).then(function (response) {
                self.iom = response.data;
                self.loading = false;
            }).catch(function () {
                self.loading = false;
            });
        },
        formatDateTime(value) {
            if (!value) {
                return '';
            }
            const date = new Date(value);
            if (isNaN(date.getTime())) {
                return value;
            }
            const pad = function (number) {
                return String(number).padStart(2, '0');
            };
            return pad(date.getDate()) + '-' + pad(date.getMonth() + 1) + '-' + date.getFullYear()
                + ' ' + pad(date.getHours()) + ':' + pad(date.getMinutes());
        },
        copyNumber() {
            const self = this;
            const fallback = function () {
                // navigator.clipboard butuh konteks aman (https/localhost) dan
                // izin; kalau tidak tersedia, pakai textarea sementara.
                const textarea = document.createElement('textarea');
                textarea.value = self.iom.number;
                textarea.setAttribute('readonly', '');
                textarea.style.position = 'absolute';
                textarea.style.left = '-9999px';
                document.body.appendChild(textarea);
                textarea.select();
                document.execCommand('copy');
                document.body.removeChild(textarea);
            };

            const notify = function () {
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$i18n.t('Memo number copied').toString(),
                    type: 'success'
                });
            };

            if (navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard.writeText(self.iom.number).then(notify).catch(function () {
                    fallback();
                    notify();
                });
            } else {
                fallback();
                notify();
            }
        },
        printMemo() {
            window.print();
        },
        deleteIom() {
            const self = this;
            if (!window.confirm(self.$i18n.t('Are you sure you want to delete this IOM?').toString())) {
                return;
            }
            self.loading = true;
            axios.delete('api/dashboard/iom/' + self.$route.params.uuid).then(function () {
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$i18n.t('IOM deleted successfully').toString(),
                    type: 'success'
                });
                self.$router.push('/dashboard/iom');
            }).catch(function () {
                self.loading = false;
            });
        },
    }
}
</script>
